import { Fragment, useEffect, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { X, PhoneCall, PhoneIncoming, PhoneOutgoing, Clock, Calendar } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { callsAPI } from '../services/api'
import { toast } from 'react-toastify'
import { format } from 'date-fns'
import clsx from 'clsx'

function CallDetailsModal({ call, isOpen, onClose }) {
  if (!call) return null

  const DirectionIcon = call.direction === 'inbound' ? PhoneIncoming : PhoneOutgoing

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A'
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const getStatusBadge = (status) => {
    const baseClasses = 'status-badge'
    
    switch (status) {
      case 'completed':
        return `${baseClasses} status-active`
      case 'failed':
      case 'busy':
      case 'no-answer':
        return `${baseClasses} status-inactive`
      case 'in-progress':
      case 'ringing':
        return `${baseClasses} status-pending`
      default:
        return `${baseClasses} bg-primary-100 text-primary-800`
    }
  }

  const queryClient = useQueryClient()
  const { data: transcriptData, refetch, isFetching } = useQuery(
    ['call-transcript', call?._id],
    () => callsAPI.getTranscript(call._id),
    { enabled: isOpen && !!call?._id }
  )

  const processMutation = useMutation(() => callsAPI.processCall(call._id), {
    onSuccess: () => {
      toast.success('Processing started')
      refetch()
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Failed to process call')
    }
  })

  const reprocessMutation = useMutation(() => callsAPI.reprocessCall(call._id), {
    onSuccess: () => {
      toast.success('Call reset. Click Process Call to re-run.')
      queryClient.invalidateQueries(['call-transcript', call._id])
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Failed to reprocess call')
    }
  })

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-primary-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white text-primary-400 hover:text-primary-500"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="sm:flex sm:items-start">
                  <div className={clsx(
                    'mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10',
                    call.direction === 'inbound' ? 'bg-green-100' : 'bg-blue-100'
                  )}>
                    <DirectionIcon className={clsx(
                      'h-6 w-6',
                      call.direction === 'inbound' ? 'text-green-600' : 'text-blue-600'
                    )} />
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                    <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-primary-900">
                      Call Details
                    </Dialog.Title>
                    
                    <div className="mt-4 space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <dt className="text-sm font-medium text-primary-500">Direction</dt>
                          <dd className="mt-1 text-sm text-primary-900 capitalize flex items-center">
                            <DirectionIcon className="h-4 w-4 mr-2" />
                            {call.direction}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-sm font-medium text-primary-500">Status</dt>
                          <dd className="mt-1">
                            <span className={getStatusBadge(call.status)}>
                              {call.status}
                            </span>
                          </dd>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <dt className="text-sm font-medium text-primary-500">From</dt>
                          <dd className="mt-1 text-sm text-primary-900">{call.from}</dd>
                        </div>
                        <div>
                          <dt className="text-sm font-medium text-primary-500">To</dt>
                          <dd className="mt-1 text-sm text-primary-900">{call.to}</dd>
                        </div>
                      </div>

                      <div>
                        <dt className="text-sm font-medium text-primary-500">Phone Number</dt>
                        <dd className="mt-1 text-sm text-primary-900">{call.phoneNumber}</dd>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <dt className="text-sm font-medium text-primary-500">Duration</dt>
                          <dd className="mt-1 text-sm text-primary-900 flex items-center">
                            <Clock className="h-4 w-4 mr-2" />
                            {formatDuration(call.duration)}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-sm font-medium text-primary-500">Application</dt>
                          <dd className="mt-1 text-sm text-primary-900">
                            {call.applicationName || 'N/A'}
                          </dd>
                        </div>
                      </div>

                      <div>
                        <dt className="text-sm font-medium text-primary-500">Start Time</dt>
                        <dd className="mt-1 text-sm text-primary-900 flex items-center">
                          <Calendar className="h-4 w-4 mr-2" />
                          {call.startTime 
                            ? format(new Date(call.startTime), 'MMM d, yyyy HH:mm:ss')
                            : format(new Date(call.createdAt), 'MMM d, yyyy HH:mm:ss')
                          }
                        </dd>
                      </div>

                      {call.endTime && (
                        <div>
                          <dt className="text-sm font-medium text-primary-500">End Time</dt>
                          <dd className="mt-1 text-sm text-primary-900">
                            {format(new Date(call.endTime), 'MMM d, yyyy HH:mm:ss')}
                          </dd>
                        </div>
                      )}

                      {call.hangupCause && (
                        <div>
                          <dt className="text-sm font-medium text-primary-500">Hangup Cause</dt>
                          <dd className="mt-1 text-sm text-primary-900">{call.hangupCause}</dd>
                        </div>
                      )}

                      {transcriptData?.data?.call?.recordingUrl && (
                        <div>
                          <dt className="text-sm font-medium text-primary-500">Recording</dt>
                          <dd className="mt-2">
                            <audio controls className="w-full">
                              <source src={transcriptData.data.call.recordingUrl} />
                              Your browser does not support the audio element.
                            </audio>
                          </dd>
                        </div>
                      )}
                      {!transcriptData?.data?.call?.recordingUrl && (
                        <div>
                          <dt className="text-sm font-medium text-primary-500">Recording</dt>
                          <dd className="mt-2">
                            <audio controls className="w-full">
                              <source src={`${import.meta.env.VITE_API_URL || '/api'}/calls/${call._id}/audio`} />
                              Your browser does not support the audio element.
                            </audio>
                          </dd>
                        </div>
                      )}

                      <div>
                        <dt className="text-sm font-medium text-primary-500">Call ID</dt>
                        <dd className="mt-1 text-xs text-primary-500 font-mono break-all">
                          {call.jambonzCallSid}
                        </dd>
                      </div>
                    </div>
                  </div>
                </div>

              <div className="mt-6 space-y-6">
                <div>
                  <h4 className="text-sm font-semibold text-primary-900">Transcript</h4>
                  {isFetching ? (
                    <div className="mt-2 text-sm text-primary-700">Loading…</div>
                  ) : transcriptData?.data?.call?.transcriptSegments?.length ? (
                    <div className="mt-2 space-y-3 max-h-64 overflow-auto pr-2">
                      {transcriptData.data.call.transcriptSegments.map((seg, idx) => (
                        <div key={idx} className="text-sm">
                          <span className="font-semibold capitalize mr-2">{seg.speaker || 'speaker'}:</span>
                          <span className="text-primary-700">{seg.text}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-2 text-sm text-primary-700 whitespace-pre-wrap bg-primary-50 rounded p-3 min-h-[4rem]">
                      {transcriptData?.data?.call?.transcript || 'No transcript yet.'}
                    </div>
                  )}
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-primary-900">Summary</h4>
                  <div className="mt-2 text-sm text-primary-700 whitespace-pre-wrap bg-primary-50 rounded p-3 min-h-[4rem]">
                    {isFetching ? 'Loading…' : (transcriptData?.data?.call?.summary || 'No summary yet.')}
                  </div>
                </div>

                {transcriptData?.data?.call?.processingStatus === 'processing' && (
                  <div className="w-full text-center text-sm text-primary-600">Auto-processing…</div>
                )}

                {!transcriptData?.data?.call?.transcript && transcriptData?.data?.call?.processingStatus !== 'processing' && (
                  <button
                    type="button"
                    className="btn-primary w-full"
                    disabled={processMutation.isLoading}
                    onClick={() => processMutation.mutate()}
                  >
                    {processMutation.isLoading ? 'Processing…' : 'Process Call'}
                  </button>
                )}

                {transcriptData?.data?.call?.transcript && (
                  <button
                    type="button"
                    className="btn-secondary w-full"
                    disabled={reprocessMutation.isLoading}
                    onClick={() => reprocessMutation.mutate()}
                  >
                    {reprocessMutation.isLoading ? 'Resetting…' : 'Reprocess Call'}
                  </button>
                )}

                <div className="sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    className="btn-secondary w-full sm:w-auto"
                    onClick={onClose}
                  >
                    Close
                  </button>
                </div>
              </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

export default CallDetailsModal